import type { CreatePaymentBody } from '@klopotun/contracts';
import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { usePaymentQuery, useSavePaymentMutation } from '../../entities/payment/index.js';
import {
  PaymentForm,
  toPaymentFormValues,
  type PaymentFormStep,
} from '../../features/payment-form/index.js';
import { useBackButton } from '../../shared/telegram/index.js';
import { ErrorState, Screen, Spinner } from '../../shared/ui/index.js';
import styles from './payment-edit-page.module.css';

export const PaymentEditPage = () => {
  const [step, setStep] = useState<PaymentFormStep>('main');
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const payment = usePaymentQuery(id);
  const savePayment = useSavePaymentMutation();
  const goBack = useCallback(() => {
    if (step === 'history') {
      setStep('main');
      return;
    }

    void navigate(-1);
  }, [step, navigate]);

  useBackButton(goBack);

  const loaded = payment.data;

  // A refetch can fail while the record is still in the cache. Replacing the form then would throw
  // away everything already typed into it, so only a missing record is worth a whole error screen.
  if (loaded === undefined) {
    return payment.isError ? (
      <Screen>
        <ErrorState
          title="Не вдалося завантажити підписку"
          message={payment.error.message}
          onRetry={() => {
            void payment.refetch();
          }}
        />
      </Screen>
    ) : (
      <Screen>
        <div className={styles.centered}>
          <Spinner label="Завантаження" />
        </div>
      </Screen>
    );
  }

  const save = (body: CreatePaymentBody): void => {
    savePayment.mutate(
      { mode: 'update', id, body },
      {
        onSuccess: () => {
          void navigate(-1);
        },
      },
    );
  };

  return (
    <Screen>
      <div className={styles.content}>
        <h1 className={styles.title}>Редагування</h1>

        {payment.isError && (
          <p className={styles.saveError} role="alert">
            Показано збережені дані: оновити не вдалося.
          </p>
        )}

        {savePayment.error !== null && (
          <p className={styles.saveError} role="alert">
            {savePayment.error.message}
          </p>
        )}

        <PaymentForm
          initialValues={toPaymentFormValues(loaded)}
          step={step}
          onStepChange={setStep}
          onSubmit={save}
          busy={savePayment.isPending}
        />
      </div>
    </Screen>
  );
};
